import api from "../api/api";

export const PCC_ACTION_URL = "https://pcc.siren24.com/pcc_V3/jsp/pcc_V3_j10_v4.jsp";
export const PCC_WINDOW_NAME = "PCCV3Window";
export const REAL_NAME_AUTH = "SRV";

export const PCC_RETURN_PATH = "/admin/sign/pccReturn";
export const RET_URL_PREFIX_OPENER = "71";
export const RET_URL_PREFIX_SELF = "72";

const PCC_RETURN_ORIGIN = process.env.REACT_APP_PCC_RETURN_ORIGIN || window.location.origin;
const PCC_WINDOW_FEATURES = "width=400, height=630, resizable=1, scrollbars=no, status=0, titlebar=0, toolbar=0, left=300, top=200";
const POPUP_BLOCKED_MESSAGE = "팝업이 차단되어 실명인증 창을 열 수 없습니다. 브라우저의 팝업 차단을 해제한 뒤 다시 시도해주세요.";
const REQUEST_FAILED_MESSAGE = "실명인증 요청 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 180000;

export interface PccTokenData {
    crypto_token_id?: string;
    integrity_value?: string;
    reqInfo?: string;
    verSion?: string;
    popupUrl?: string;
}

export interface PccTokenResponse {
    success?: boolean;
    error?: string;
    data?: PccTokenData;
}

export interface PccUserInfo {
    name?: string;
    phone?: string;
    sex?: string;
    birthday?: string;
}

interface PccRequestOptions {
    admin?: boolean;
    retUrl?: string;
    certGb?: string;
    onError?: (message: string) => void;
    onResult?: (info: PccUserInfo) => void;
}

export const onlyDigits = (value: string) => (value || '').replace(/\D/g, '');

export function buildRetUrl(path: string = PCC_RETURN_PATH, prefix: string = RET_URL_PREFIX_SELF) {
    return prefix + PCC_RETURN_ORIGIN + path;
}

export function openPccWindow(): Window | null {
    return window.open("", PCC_WINDOW_NAME, PCC_WINDOW_FEATURES);
}

export async function requestPcc(form: HTMLFormElement | null, options: PccRequestOptions = {}): Promise<void> {
    const notifyError = options.onError || ((message: string) => window.alert(message));
    if (!form) {
        notifyError(REQUEST_FAILED_MESSAGE);
        return;
    }
    const popup = openPccWindow();
    if (popup == null) {
        notifyError(POPUP_BLOCKED_MESSAGE);
        return;
    }
    try {
        const body = { retUrl: options.retUrl || buildRetUrl(), certGb: options.certGb || "H" };
        const res: PccTokenResponse = options.admin
            ? await api.getAdminPccToken(body)
            : await api.getPccToken(body);

        if (!res || !res.success || !res.data) {
            throw new Error((res && res.error) || "실명인증 요청정보를 받지 못했습니다.");
        }
        const data = res.data;
        if (!data.crypto_token_id || !data.reqInfo) {
            throw new Error("실명인증 요청정보가 비어 있습니다.");
        }
        setFieldValue(form, "crypto_token_id", data.crypto_token_id);
        setFieldValue(form, "integrity_value", data.integrity_value || "");
        setFieldValue(form, "reqInfo", data.reqInfo);
        setFieldValue(form, "verSion", data.verSion || "3");
        form.action = data.popupUrl || PCC_ACTION_URL;
        form.target = PCC_WINDOW_NAME;
        form.submit();

        if (options.onResult) {
            watchPccResult(popup, options.admin, options.onResult, data.crypto_token_id);
        }
    } catch (error) {
        console.error(error);
        popup.close();
        notifyError(REQUEST_FAILED_MESSAGE);
    }
}

export function watchPccResult(popup: Window | null, admin: boolean | undefined, onResult: (info: PccUserInfo) => void, cryptoTokenId?: string) {
    const startedAt = Date.now();
    let closedAt = 0;
    const timer = window.setInterval(async () => {
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            window.clearInterval(timer);
            return;
        }
        if (popup && popup.closed) {
            if (!closedAt) {
                closedAt = Date.now();
            } else if (Date.now() - closedAt > 3000) {
                window.clearInterval(timer);
                return;
            }
        }
        let info: PccUserInfo | null = null;
        try {
            info = admin ? await api.getAdminPccInfo(cryptoTokenId) : await api.getPccInfo(cryptoTokenId);
        } catch (error) {
            return;
        }
        if (!info || !info.phone) {
            return;
        }
        window.clearInterval(timer);
        try {
            if (popup && !popup.closed) { popup.close(); }
        } catch (error) {
            console.error(error);
        }
        onResult(info);
    }, POLL_INTERVAL_MS);
}

function setFieldValue(form: HTMLFormElement, name: string, value: string) {
    const field = form.elements.namedItem(name) as HTMLInputElement | null;
    if (!field) {
        throw new Error("실명인증 요청 항목이 없습니다. [" + name + "]");
    }
    field.value = value;
}
